import {
	Button,
	ButtonGroup,
	Classes,
	ControlGroup,
	Dialog,
	HTMLTable,
	InputGroup,
	Tag
} from "@blueprintjs/core";
import React from "react";
import CountResult from "common/entities/CountResult";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import { FlexJustify } from "common/fragments/Flex";
import VerticalCenter from "common/fragments/VerticalCenter";
import IOption from "common/models/IOption";
import Query from "core/api/QueryComponent";
import AppToaster from "core/util/AppToaster";
import Class from "core/util/Class";
import IEntity from "core/util/IEntity";
import { clamp } from "core/util/MathUtils";
import DeleteButton from "core/components/DeleteButton";
import EntityForm from "core/components/EntityForm";
import Label from "core/components/label/Label";
import Paging from "core/components/Paging";

interface ITableCellProps<T> {
	data: T;
	reloadList: (resetPaging?: boolean) => void;
}

export interface ITableColumn<T> {
	key: React.Key;
	label?: React.ReactNode;
	sort?: string;
	renderCell: (props: ITableCellProps<T>) => React.ReactNode;
}

export interface IPageableListQuery<T> {
	entities: null | T[];
	count: CountResult;
}

interface IEntityTableProps<T extends IEntity> {
	pagingQuery: Class<IPageableListQuery<T>>;
	formClass: Class<T>;
	params?: object;
	columns: Array<ITableColumn<T>>;
	renderFilters?: (
		filters: object,
		onChange: (filters: object) => void
	) => JSX.Element;

	// EDITING

	canCreate?: boolean; // = true
	createExcludeFields?: Array<keyof T>;
	getInitialCreateData?: () => Partial<T>;

	canUpdate?: boolean | ((o: T) => boolean); // = true
	showPaginationOptions?: boolean | ((o: T) => boolean); // = true
	updateExcludeFields?: Array<keyof T> | ((o: T) => Array<keyof T>);

	canDelete?: boolean | ((o: T) => boolean); // = true

	onAfterRefetch?: (o: T) => T;
	onBeforeSave?: (o: T) => boolean | object;

	editParams?: object;
}

interface IEntityTableState<T> {
	pageSize: number;
	pageNumber: number;
	sort: null | string;
	sortDesc: boolean;
	query: string;
	filters: object;
	editing: null | { type: "create" | "update"; data?: T };
	saving: boolean;
	clickedChar: string;
}

const pageSizeOptions: Array<IOption<number>> = [
	{ value: 10, label: "Show 10" },
	{ value: 25, label: "Show 25" }
];

export class GlossaryTable<T extends IEntity> extends React.Component<
	IEntityTableProps<T>,
	IEntityTableState<T>
> {
	private skeletons: number;

	public constructor(props: IEntityTableProps<T>) {
		super(props);
		this.skeletons = 10;
		this.state = {
			pageSize: 10,
			pageNumber: 0,
			sort: null,
			sortDesc: false,
			query: "",
			filters: {},
			editing: null,
			saving: false,
			clickedChar: window.location.hash.slice(1)
		};
	}

	private getTerm = data => {
		const id = data.term ? data.term[0].toUpperCase() : "";
		return id;
	};

	private clickedAlphabet = char => {
		this.setState({ clickedChar: char });
		this.letterDisplay(char);
	};

	private letterDisplay = char => {
		setTimeout(() => {
			if (document.querySelector(".section" + char)) {
				var element = document.querySelector(".section" + char);
				element.scrollIntoView();
			}
		}, 0);
		const alphabet = [
			"A",
			"B",
			"C",
			"D",
			"E",
			"F",
			"G",
			"H",
			"I",
			"J",
			"K",
			"L",
			"M",
			"N",
			"O",
			"P",
			"Q",
			"R",
			"S",
			"T",
			"U",
			"V",
			"W",
			"X",
			"Y",
			"Z"
		];
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					gap: "10px"
				}}
			>
				{alphabet.map(data => {
					data == char;
					return (
						<a
							className={data}
							href={"#" + data}
							onClick={() => this.clickedAlphabet(data)}
							style={
								data == char
									? {
											height: "25px",
											width: "25px",
											backgroundColor: "#bbb",
											borderRadius: "50%",
											display: "inline-block",
											textAlign: "center"
									  }
									: {
											textAlign: "center"
									  }
							}
						>
							{data}
						</a>
					);
				})}
			</div>
		);
	};

	public render() {
		const {
			canCreate = true,
			canDelete = true,
			canUpdate = true,
			showPaginationOptions = true,
			columns = [],
			formClass,
			pagingQuery,
			params,
			renderFilters
		} = this.props;

		let p = {
			// pageSize: this.state.pageSize,
			// pageNumber: this.state.pageNumber
		};

		if (this.state.sort) {
			if (this.state.sortDesc) {
				p["sort"] = "-" + this.state.sort;
			} else {
				p["sort"] = this.state.sort;
			}
		}

		if (this.state.query) {
			p["query"] = this.state.query;
		}

		if (params) {
			p = { ...p, ...params };
		}

		return (
			<Query.Root entity={pagingQuery} args={p}>
				{args => {
					const rows: React.ReactNodeArray = [];
					let total: number = 0;
					let start: number = 0;
					let end: number = 0;

					if (args.loading) {
						// loading
						let i = 0;
						while (i < this.skeletons) {
							rows.push(
								<tr key={i}>
									{columns.map(col => {
										return (
											<td key={col.key}>
												<p className={Classes.SKELETON}>
													{"..."}
												</p>
											</td>
										);
									})}
									<td key="edit">
										<p className={Classes.SKELETON}>
											{"..."}
										</p>
									</td>
								</tr>
							);
							i++;
						}
					} else if (
						args.data &&
						args.data.entities &&
						args.data.entities.length > 0
					) {
						// results
						total = args.data.count.count;
						start = 1 + this.state.pageSize * this.state.pageNumber;
						end = clamp(
							start,
							total,
							start + this.state.pageSize - 1
						);

						this.skeletons = args.data.entities.length;
						let charArr = [];
						args.data.entities.forEach(o => {
							const key = o.id;
							const id = this.getTerm(o);
							const canUpdateItem =
								(typeof canUpdate === "function" &&
									canUpdate(o)) ||
								(typeof canUpdate === "boolean" && canUpdate);

							const canDeleteItem =
								(typeof canDelete === "function" &&
									canDelete(o)) ||
								(typeof canDelete === "boolean" && canDelete);
							/**Inserting Character */
							if (charArr[charArr.length - 1] != id) {
								charArr.push(id);
								rows.push(
									<tr id={id} className={"section" + id}>
										<td>
											<a
												style={{ fontWeight: 600 }}
												onClick={() =>
													this.clickedAlphabet(id)
												}
												href={"#" + id}
											>
												{id}
											</a>
										</td>
										<td />
										<td />
									</tr>
								);
							}
							/**Inserting Actual table row data */
							rows.push(
								<tr key={key} id={id}>
									{columns.map(col => {
										const cellContent = col.renderCell({
											data: o,
											reloadList: args.forceRefresh
										});

										return (
											<td key={col.key}>{cellContent}</td>
										);
									})}
									<td
										key="edit"
										style={{
											textAlign: "right"
										}}
									>
										<ButtonGroup>
											{canUpdateItem && (
												<Button
													text={
														<Label
															k="edit"
															v="Edit"
														/>
													}
													onClick={this.getOpenUpdateHandler(
														o
													)}
												/>
											)}
											{canDeleteItem && (
												<DeleteButton
													entity={formClass}
													params={{
														id: o.id
													}}
													onDeleted={
														args.forceRefresh
													}
												/>
											)}
										</ButtonGroup>
									</td>
								</tr>
							);
						});
					} else {
						this.skeletons = 1;
						// no results
						rows.push(
							<tr key="noresults">
								<td colSpan={columns.length}>
									<VerticalCenter>No Results</VerticalCenter>
								</td>
							</tr>
						);
					}

					return (
						<div>
							<FlexJustify
								style={{
									marginBottom: "15px"
								}}
							>
								<ControlGroup>
									<InputGroup
										placeholder="Search"
										value={this.state.query}
										onChange={(e: any) =>
											this.setQuery(e.target.value)
										}
									/>
									<Button
										icon="small-cross"
										onClick={() => this.setQuery("")}
									/>
								</ControlGroup>
								<div>
									{typeof renderFilters === "function" &&
										renderFilters(
											this.state.filters,
											this.onChangeFilters
										)}
								</div>
								{canCreate && (
									<div>
										<Button
											text={
												<Label k="create" v="Create" />
											}
											icon="small-plus"
											onClick={this.openCreate}
										/>
									</div>
								)}
							</FlexJustify>
							{/* <div>
								<Tag
									minimal={true}
									intent={total > 0 ? "primary" : "none"}
									className={
										(args.loading && Classes.SKELETON) || ""
									}
								>
									<Label
										k="paging.results.count"
										v="${start} - ${end} of ${total} Results"
										args={{
											total,
											start,
											end
										}}
									/>
								</Tag>
							</div> */}
							{this.letterDisplay(this.state.clickedChar)}
							<HTMLTable striped={true} style={{ width: "100%" }}>
								<thead>
									<tr>
										{columns.map(this.renderColumnHeader)}
									</tr>
								</thead>
								<tbody>{rows}</tbody>
							</HTMLTable>
							{/* <FlexJustify
								style={{
									paddingTop: "15px"
								}}
							>
								<div>
									<Paging
										pageSize={this.state.pageSize}
										pageNumber={this.state.pageNumber}
										total={total}
										setPageNumber={this.onSetPageNumber}
									/>
								</div>
								{showPaginationOptions ? (
									<DropdownSelect
										options={pageSizeOptions}
										value={this.state.pageSize}
										onChange={this.onSetPageSize}
									/>
								) : (
									""
								)}
							</FlexJustify> */}

							<Dialog
								isOpen={!!this.state.editing}
								onClose={this.close}
								title={
									this.state.editing &&
									this.state.editing.type === "update" ? (
										<Label k="edit" v="Edit" />
									) : (
										<Label k="create" v="Create" />
									)
								}
							>
								<div className={Classes.DIALOG_BODY}>
									{!!this.state.editing &&
										this.renderForm(args.forceRefresh)}
								</div>
							</Dialog>
						</div>
					);
				}}
			</Query.Root>
		);
	}

	private renderColumnHeader = (col: ITableColumn<T>) => {
		return (
			<th key={col.key}>
				{col.sort ? (
					<a onClick={this.getSortHandler(col.sort)}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center"
							}}
						>
							<span>{col.label}</span>
						</div>
					</a>
				) : (
					col.label
				)}
			</th>
		);
	};

	private renderForm = (refresh: () => void) => {
		if (!this.state.editing) {
			return null;
		}

		const {
			createExcludeFields,
			getInitialCreateData,
			onBeforeSave,
			onAfterRefetch,
			updateExcludeFields
		} = this.props;

		let formData: T;

		const isUpdate = this.state.editing.type === "update";

		if (this.state.editing.type === "update") {
			formData = this.state.editing.data || ({} as T);
		} else {
			formData = ((typeof getInitialCreateData === "function" &&
				getInitialCreateData()) ||
				{}) as T;
		}

		let excludeFields;

		if (isUpdate && updateExcludeFields) {
			excludeFields =
				typeof updateExcludeFields === "function"
					? updateExcludeFields(formData)
					: updateExcludeFields;
		} else {
			excludeFields = createExcludeFields;
		}

		return (
			<EntityForm
				clazz={this.props.formClass}
				instance={formData}
				refetch={isUpdate}
				supplementaryParams={this.props.editParams}
				excludedFields={excludeFields}
				afterRefetch={onAfterRefetch}
				beforeSave={onBeforeSave}
				afterSave={() => {
					this.setState(
						{
							editing: null
						},
						() => {
							AppToaster.show({
								message: "Saved Successfully",
								intent: "success"
							});
							refresh();
						}
					);
				}}
			/>
		);
	};

	private getSortHandler = (sort: string) => () => {
		if (sort === this.state.sort) {
			this.setState({ sortDesc: !this.state.sortDesc });
		} else {
			this.setState({ sort, sortDesc: false });
		}
	};

	private setQuery = (query: string) =>
		this.setState({ query, pageNumber: 0 });
	private onSetPageSize = (pageSize: number) =>
		this.setState({ pageSize, pageNumber: 0 });
	private onSetPageNumber = (pageNumber: number) =>
		this.setState({ pageNumber });

	private onChangeFilters = (filters: object) =>
		this.setState({ filters, pageNumber: 0 });

	private openCreate = () => this.setState({ editing: { type: "create" } });

	private close = () => this.setState({ editing: null });

	private getOpenUpdateHandler = (data: T) => () => {
		const copy = Object.entries(data).reduce(
			(a, [key, value]) => {
				if (value !== null) {
					a[key] = value;
				}
				return a;
			},
			{} as T
		);
		this.setState({
			editing: { type: "update", data: copy }
		});
	};
}



// WEBPACK FOOTER //
// ./src/panels/kb/glossary/GlossaryTable.tsx